
import { useState, useEffect } from 'react';
import { supabase, PastEvent } from '../../../lib/supabase';
import PastEventModal from './PastEventModal';

export default function PastEventsManager() {
  const [events, setEvents] = useState<PastEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<PastEvent | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('past_events')
      .select('*')
      .order('event_date', { ascending: false });
    setEvents(data || []);
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    await supabase.from('past_events').delete().eq('id', id);
    setEvents(prev => prev.filter(e => e.id !== id));
    setDeletingId(null);
    setConfirmDeleteId(null);
  };

  const handleEdit = (event: PastEvent) => {
    setEditingEvent(event);
    setModalOpen(true);
  };

  const handleNew = () => {
    setEditingEvent(null);
    setModalOpen(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-white text-xl font-bold font-['Montserrat']">Eventos Pasados</h2>
          <p className="text-gray-500 text-sm mt-1">{events.length} evento{events.length !== 1 ? 's' : ''} registrado{events.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={handleNew}
          className="flex items-center gap-2 bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-bold text-sm px-5 py-2.5 rounded-lg transition-all shadow-[0_0_15px_rgba(217,70,239,0.3)] hover:shadow-[0_0_25px_rgba(217,70,239,0.5)] cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line text-base"></i>
          </div>
          Nuevo evento
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-fuchsia-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-gray-800 rounded-xl">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 text-gray-700">
            <i className="ri-history-line text-4xl"></i>
          </div>
          <p className="text-gray-500 text-sm">No hay eventos pasados registrados.</p>
          <button
            onClick={handleNew}
            className="mt-4 text-fuchsia-400 hover:text-fuchsia-300 text-sm underline cursor-pointer"
          >
            Añadir el primero
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {events.map(event => (
            <div
              key={event.id}
              className="bg-gray-900 border border-gray-800 hover:border-gray-700 rounded-xl overflow-hidden transition-all group"
            >
              {event.image_url ? (
                <div className="w-full h-36 overflow-hidden">
                  <img
                    src={event.image_url}
                    alt={event.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ) : (
                <div className="w-full h-36 bg-gray-800 flex items-center justify-center">
                  <div className="w-10 h-10 flex items-center justify-center text-gray-700">
                    <i className="ri-image-line text-3xl"></i>
                  </div>
                </div>
              )}

              <div className="p-4">
                <h3 className="text-white font-semibold text-sm truncate mb-1">{event.title}</h3>
                <div className="flex items-center gap-3 mb-3">
                  <span className="flex items-center gap-1 text-gray-500 text-xs">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-calendar-line text-xs"></i>
                    </div>
                    {new Date(event.event_date + 'T00:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                  <span className="flex items-center gap-1 text-gray-500 text-xs">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-map-pin-line text-xs"></i>
                    </div>
                    {event.location}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(event)}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white text-xs py-2 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-edit-line text-xs"></i>
                    Editar
                  </button>
                  {confirmDeleteId === event.id ? (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleDelete(event.id)}
                        disabled={deletingId === event.id}
                        className="bg-red-600 hover:bg-red-500 text-white text-xs px-3 py-2 rounded-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                      >
                        {deletingId === event.id ? '...' : 'Eliminar'}
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="bg-gray-700 text-gray-300 text-xs px-3 py-2 rounded-lg cursor-pointer whitespace-nowrap"
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteId(event.id)}
                      className="w-8 h-8 flex items-center justify-center bg-gray-800 hover:bg-red-900/40 text-gray-400 hover:text-red-400 rounded-lg transition-all cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <PastEventModal
          event={editingEvent}
          onClose={() => setModalOpen(false)}
          onSaved={fetchEvents}
        />
      )}
    </div>
  );
}
